/**
 * Scoring, stars, XP and learner ranks.
 *
 * The measurement rules match the rest of TypeSwift so a child's kids-mode WPM
 * is directly comparable with the main typing test: one "word" is five
 * characters, and speed is reported net of uncorrected errors.
 */
import type { EngineSnapshot, LessonDef, LessonScore } from '../types';
import { elapsedSeconds, typedCorrectChars } from './engine';

/** Words per minute from a character count. One word = five characters. */
export function wpmFromChars(chars: number, seconds: number): number {
  if (seconds <= 0) return 0;
  return Math.round((chars / 5 / seconds) * 60);
}

export function accuracyPct(correctFirstTry: number, keystrokes: number): number {
  if (keystrokes <= 0) return 100;
  return Math.round((correctFirstTry / keystrokes) * 1000) / 10;
}

/**
 * Stars.
 *   1★ you finished it
 *   2★ you finished it accurately
 *   3★ you finished it accurately *and* at the level's pace
 * Speed alone never earns a star — that is deliberate, and it is the single
 * most important pedagogical decision in the module.
 */
export function starsFor(wpm: number, accuracy: number, lesson: LessonDef): 0 | 1 | 2 | 3 {
  if (accuracy >= lesson.targetAccuracy && wpm >= lesson.targetWpm) return 3;
  if (accuracy >= lesson.targetAccuracy) return 2;
  return 1;
}

/** XP rewards effort *and* quality, so grinding an easy lesson is not optimal. */
export function xpFor(chars: number, stars: number, accuracy: number): number {
  const effort = Math.round(chars / 8);
  const quality = stars * 15;
  const precision = accuracy >= 98 ? 12 : accuracy >= 95 ? 6 : 0;
  return Math.max(5, effort + quality + precision);
}

export function coinsFor(stars: number, accuracy: number): number {
  return 4 + stars * 3 + (accuracy >= 98 ? 3 : 0);
}

/** The three characters missed most often in this run — feeds the summary. */
export function troubleChars(snap: EngineSnapshot, limit = 3): string[] {
  return Object.entries(snap.keyStats)
    .filter(([, s]) => s.wrong > 0)
    .sort((a, b) => b[1].wrong - a[1].wrong || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([char]) => char);
}

/**
 * The fastest sustained human typing is around 15 keystrokes per second, and
 * that is a world record — a child will never approach it. An elapsed time
 * shorter than `keystrokes / 15` therefore means the clock is wrong, not that
 * the typing was superhuman: a paste, a held key auto-repeating, a wake from
 * sleep, or an automated run. Flooring the elapsed time there keeps a
 * two-second warm-up from reporting 700 words per minute.
 */
const MAX_KEYSTROKES_PER_SECOND = 15;

/**
 * A run has to be substantial before its speed means anything. Nine seconds of
 * "fff jjj fjf" is not a typing speed, it is a warm-up, and it must not be
 * able to set a lifetime record or unlock a speed badge.
 */
export const QUALIFYING_CHARS = 80;

export function isQualifyingRun(run: { chars: number }): boolean {
  return run.chars >= QUALIFYING_CHARS;
}

export function scoreLesson(snap: EngineSnapshot, lesson: LessonDef, now: number): LessonScore {
  const seconds = Math.max(elapsedSeconds(snap, now), snap.keystrokes / MAX_KEYSTROKES_PER_SECOND);
  const correctChars = typedCorrectChars(snap);
  const netWpm = wpmFromChars(correctChars, seconds);
  const wpm = wpmFromChars(snap.keystrokes, seconds);
  const accuracy = accuracyPct(snap.correctFirstTry, snap.keystrokes);
  const stars = starsFor(netWpm, accuracy, lesson);

  return {
    wpm,
    netWpm,
    accuracy,
    mistakes: snap.mistakes,
    keystrokes: snap.keystrokes,
    seconds: Math.round(seconds),
    chars: correctChars,
    stars,
    xp: xpFor(correctChars, stars, accuracy),
    coins: coinsFor(stars, accuracy),
    trouble: troubleChars(snap),
  };
}

/* ─────────────────────────────────────────────────────────── learner rank ── */

export interface Rank {
  level: number;
  name: string;
  emoji: string;
  /** XP at which this rank begins. */
  from: number;
}

export const RANKS: Rank[] = [
  { level: 1, name: 'Sprout', emoji: '🌱', from: 0 },
  { level: 2, name: 'Explorer', emoji: '🧭', from: 150 },
  { level: 3, name: 'Adventurer', emoji: '🎒', from: 400 },
  { level: 4, name: 'Ranger', emoji: '🏹', from: 800 },
  { level: 5, name: 'Pilot', emoji: '✈️', from: 1400 },
  { level: 6, name: 'Captain', emoji: '⚓', from: 2200 },
  { level: 7, name: 'Navigator', emoji: '🗺️', from: 3200 },
  { level: 8, name: 'Ace', emoji: '⭐', from: 4500 },
  { level: 9, name: 'Champion', emoji: '🏆', from: 6200 },
  { level: 10, name: 'Legend', emoji: '👑', from: 8500 },
];

export interface RankState {
  rank: Rank;
  next: Rank | null;
  /** 0–1 through the current rank. */
  progress: number;
  xpIntoRank: number;
  xpForRank: number;
}

export function rankFor(xp: number): RankState {
  let index = 0;
  for (let i = 0; i < RANKS.length; i++) if (xp >= RANKS[i].from) index = i;
  const rank = RANKS[index];
  const next = RANKS[index + 1] ?? null;
  const span = next ? next.from - rank.from : 1;
  const into = xp - rank.from;
  return {
    rank,
    next,
    xpIntoRank: into,
    xpForRank: next ? span : into,
    progress: next ? Math.min(1, into / span) : 1,
  };
}

/* ────────────────────────────────────────────────────────────── benchmarks ── */

/**
 * Published typing-speed expectations by age. Used to give a child honest,
 * age-appropriate context instead of comparing them with adults. These are
 * teaching guidelines, not test norms — the UI labels them as such.
 */
export const AGE_BENCHMARKS: { band: string; label: string; typical: number; strong: number }[] = [
  { band: '5-7', label: 'Ages 5–7', typical: 8, strong: 15 },
  { band: '8-10', label: 'Ages 8–10', typical: 15, strong: 25 },
  { band: '11-14', label: 'Ages 11–14', typical: 25, strong: 40 },
  { band: 'unspecified', label: 'All learners', typical: 20, strong: 35 },
];

export function benchmarkFor(band: string) {
  return AGE_BENCHMARKS.find((b) => b.band === band) ?? AGE_BENCHMARKS[3];
}
